import React, { useEffect, useMemo, useState } from 'react';
import './ODProductCardV2.scss';
import {
  getMarketPrice,
  getProductPrice,
  removeDomainName,
  wishListHandlerHelper,
} from '../../helper';
import { useDispatch, useSelector } from 'react-redux';
import OdHeartFilledIcon from '../../../../static/assets/svg/od-heart-filled-icon.svg';
import { setToastMessage, shortlist } from '../../../actionCreators/common';
import { isEmptyObject } from '../../../utils/helper';
import { withRouter } from 'react-router';

const ODProductCardV2 = React.memo(function ODProductCardV2({
  product,
  productSelectCallback,
  indexOfProduct,
  device,
  theme = '',
}) {
  const defaultImage = useMemo(() => {
    return (
      product?.image_url ||
      'https://static.lenskart.com/media/owndays/desktop/img/od-misc/no-image.jpg'
    );
  }, [product?.image_url]);

  const lensPerBox = useMemo(() => {
    if (product?.classification !== 'contact_lens') return null;

    return (
      product?.hashtagList.find(({ property }) => property === 'contact_packaging')?.name || null
    );
  }, [product]);

  const [slMessage, setSlMessage] = useState({});
  const [productImage, setProductImage] = useState(defaultImage);
  const shortListData = useSelector(state => state.common.shortListData);
  const wishlistCount = useSelector(state => state.common.isWishlistCount);
  const login = useSelector(state => state.common.login);
  const dataLocale = useSelector(state => state.locale);
  const userInfo = useSelector(state => state.common.userInfo);
  const categoryData = useSelector(state => state.category?.categoryData?.result);

  const dispatch = useDispatch();

  const { CURRENCY_SYMBOL, CURRENCY_CODE, LENS_PER_BOX } = dataLocale;

  const shortListProduct = shortListData?.result?.productIds.concat(
    shortListData.result.productList.map(({ id }) => id)
  );

  const isShortlisted = shortListProduct?.indexOf(product?.id) > -1;

  useEffect(() => {
    if (!isEmptyObject(slMessage) && wishlistCount !== 50) {
      dispatch(setToastMessage(slMessage));
    }
  }, [dispatch, slMessage, wishlistCount]);

  const wishListHandler = event => {
    wishListHandlerHelper(
      event,
      product,
      wishlistCount,
      dataLocale,
      login,
      dispatch,
      setToastMessage,
      shortlist,
      setSlMessage,
      shortListData,
      userInfo,
      categoryData,
      indexOfProduct
    );
  };

  const handleProductClick = url => {
    if (productSelectCallback) {
      productSelectCallback(indexOfProduct);
    }
    // Add Item to recently viewed local storage
    const viewedProducts = localStorage.getItem('recentlyViewedProducts')
      ? JSON.parse(localStorage.getItem('recentlyViewedProducts'))
      : [];

    if (viewedProducts.length >= 8) {
      viewedProducts.pop();
    }

    const remainingProducts = viewedProducts.filter(obj => obj.id !== product?.id);
    const finalProducts = [product, ...remainingProducts];
    localStorage.setItem('recentlyViewedProducts', JSON.stringify(finalProducts));

    // Redirect url
    if (url?.length > 0 && url.includes('sg/en/')) {
      window.open(`/${removeDomainName(url)}`, '_blank');
    } else {
      window.open(`/sg/en/${removeDomainName(url)}`, '_blank');
    }
  };

  const onMouseEnter = () => {
    setProductImage(
      product?.hover_image_url ||
        'https://static.lenskart.com/media/owndays/desktop/img/od-misc/no-image.jpg'
    );
  };

  const onMouseLeave = () => {
    setProductImage(
      product?.image_url ||
        'https://static.lenskart.com/media/owndays/desktop/img/od-misc/no-image.jpg'
    );
  };

  const onMouseColorEnterHandler = color => {
    const colorImg =
      color?.image_urls?.[0] ||
      'https://static.lenskart.com/media/owndays/desktop/img/od-misc/no-image.jpg';

    const tempImage = new Image();
    tempImage.src = colorImg;

    tempImage.onload = () => {
      setProductImage(colorImg);
    };

    tempImage.onerror = () => {
      setProductImage('https://static.lenskart.com/media/owndays/desktop/img/od-misc/no-image.jpg');
    };
  };

  const onMouseColorLeaveHandler = () => {
    setProductImage(defaultImage);
  };

  if (product) {
    const price = getProductPrice(product);
    const marketPrice = getMarketPrice(product);
    const brandName = product?.brand_name;
    const colorOptions = product?.color_options;
    const productUrl = product?.product_url;
    const modelNumber = product?.model_name;
    const sku = product?.colorCode;

    return (
      <div
        className={`owndays-product-card ${theme === 'v2' ? 'owndays-product-card__v2' : ''}`}
        onClick={() => handleProductClick(productUrl)}
      >
        <div className="owndays-product-card__image__wrapper">
          <img
            alt="owndays"
            className="owndays-product-card__image"
            src={
              productImage ||
              'https://static.lenskart.com/media/owndays/desktop/img/od-misc/no-image.jpg'
            }
            onError={({ currentTarget }) => {
              currentTarget.onerror = null;
              currentTarget.src =
                'https://static.lenskart.com/media/owndays/desktop/img/od-misc/no-image.jpg';
            }}
            onMouseEnter={!(device === 'mobile') && onMouseEnter}
            onMouseLeave={!(device === 'mobile') && onMouseLeave}
          />
        </div>

        <div className="owndays-product-card__desc">
          {product?.classification !== 'gift_cards' ? (
            <div className="owndays-product-card__wishlist__wrapper">
              <img
                alt="wishlist"
                className="owndays-product-card__wishlist"
                src={
                  isShortlisted
                    ? OdHeartFilledIcon
                    : 'https://static.lenskart.com/media/owndays/desktop/img/od-misc/od-heart-icon.svg'
                }
                onClick={wishListHandler}
              />
            </div>
          ) : null}

          <p className="owndays-product-card__title">{brandName}</p>
          <p className="owndays-product-card__sku">
            {product?.classification === 'contact_lens'
              ? lensPerBox && `| ${lensPerBox} ${LENS_PER_BOX}`
              : `${modelNumber}`}
            {` ${sku}`}
          </p>
        </div>

        <p
          className={`owndays-product-card__price ${
            product?.offersClarity ? 'owndays-product-card__price--sale' : ''
          }`}
        >
          <div className="owndays-product-card__market-and-lenskart-price">
            {product?.offersClarity && (
              <span className="owndays-product-card__sale-tag owndays-product-card__sale-tag--red">
                {product?.offersClarity?.options?.[0]?.offerHeading}
              </span>
            )}
            <span>{`${CURRENCY_CODE}${CURRENCY_SYMBOL}${price}`}</span>
            {price < marketPrice ? (
              <span className="owndays-product-card__market-and-lenskart-price-mp">{`${CURRENCY_CODE}${CURRENCY_SYMBOL}${marketPrice}`}</span>
            ) : null}
          </div>
        </p>

        {!['gift_cards', 'contact_lens'].includes(product?.classification) ? (
          <div className="owndays-product-card__color__options">
            {colorOptions?.length > 0 &&
              colorOptions.map((color, index) => {
                return (
                  <div key={index} className="owndays-product-card__color__options__item__wrapper">
                    <div
                      className="owndays-product-card__color__options__item"
                      style={{
                        background: color?.frameColorImage
                          ? `url(${color?.frameColorImage})`
                          : color?.color || '',
                      }}
                      onClick={event => {
                        event.stopPropagation();
                        handleProductClick(color?.product_url);
                      }}
                      onMouseEnter={() => onMouseColorEnterHandler(color)}
                      onMouseLeave={onMouseColorLeaveHandler}
                    />
                  </div>
                );
              })}
          </div>
        ) : null}
      </div>
    );
  }

  return null;
});

export default withRouter(ODProductCardV2);
